import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ClipboardList, FileText, Landmark, UserCheck, ScrollText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { RETAINR, SOCIALS, IMAGES } from '../mock/data';
import SocialIcon from '../components/SocialIcon';

const FEATURE_ICONS = [ClipboardList, FileText, Landmark, UserCheck, ScrollText];

const RetainRPage = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="retainr-page">
      {/* HERO */}
      <section className="relative overflow-hidden paper-texture" style={{ paddingTop: '150px', paddingBottom: '6rem' }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${IMAGES.capitol})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            opacity: 0.08,
            filter: 'grayscale(60%) brightness(0.7)',
          }}
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(10,14,26,0.4) 0%, #0A0E1A 90%)' }} />

        <div className="container-custom relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="lg:col-span-7"
          >
            <div className="label-small mb-5">{t.retainr.label}</div>
            <div className="font-heading font-black leading-none" data-testid="retainr-wordmark" style={{ fontSize: 'clamp(3.5rem, 9vw, 7.5rem)', letterSpacing: '-0.03em' }}>
              <span className="text-[#F0EDE8]">Retain</span>
              <span className="metallic-gold italic">R</span>
            </div>
            <h1 className="font-heading italic text-2xl md:text-3xl text-[#DCCFAD] mt-6 leading-snug">
              {t.retainr.heading}
            </h1>
            <div className="gold-rule mt-8 mb-8" />
            <p className="text-[#DCCFAD] text-base md:text-lg leading-relaxed max-w-2xl">{RETAINR.summary}</p>
            <div className="flex flex-wrap items-center gap-5 mt-10">
              <a href={RETAINR.url} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="retainr-visit-button">
                {t.retainr.visit}
                <ArrowUpRight size={14} />
              </a>
              <span className="text-[11px] tracking-[0.3em] uppercase text-[#8a7a54]">{t.retainr.founderNote}</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            className="lg:col-span-5"
          >
            <div className="portrait-frame">
              <div className="chiaroscuro relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
                <img src={IMAGES.office} alt="Reviewing a case file with clients" className="w-full h-full object-cover" style={{ objectPosition: 'center 25%' }} />
                <div className="absolute bottom-0 left-0 right-0 p-5 z-10" style={{ background: 'linear-gradient(to top, rgba(10,14,26,0.95), transparent)' }}>
                  <div className="text-[9px] tracking-[0.4em] uppercase text-[#8a7a54]">{t.retainr.mattersLabel}</div>
                  <div className="font-heading text-[#C4A35A] text-base mt-1">Family Law &middot; Pro Se &middot; Attorney-Ready</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section relative" style={{ background: 'linear-gradient(180deg, #0A0E1A 0%, #0c1220 55%, #0A0E1A 100%)' }}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="label-small mb-4">{t.retainr.featuresLabel}</div>
              <p className="text-[#DCCFAD] leading-relaxed text-[15px] md:text-base">{RETAINR.description}</p>
              <div className="mt-10">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-3">{t.retainr.mattersLabel}</div>
                <div className="flex flex-wrap gap-2">
                  {RETAINR.matters.map((m) => (
                    <span key={m} className="tag-pill" data-testid="retainr-matter-pill">{m}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {RETAINR.features.map((f, i) => {
                const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.07 }}
                    className={`border border-[rgba(196,163,90,0.25)] bg-[rgba(196,163,90,0.03)] p-7 hover:border-[#C4A35A] hover:bg-[rgba(196,163,90,0.07)] transition-colors ${i === 4 ? 'sm:col-span-2' : ''}`}
                    data-testid="retainr-feature-card"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <Icon size={18} className="text-[#C4A35A]" />
                      <span className="font-heading text-[#C4A35A] text-sm">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl text-[#F0EDE8] leading-tight">{f.title}</h3>
                    <p className="text-[#DCCFAD] text-[14px] mt-3 leading-relaxed">{f.body}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* COURTSIDE WITH KC */}
      <section className="section relative paper-texture overflow-hidden" data-testid="courtside-section">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${IMAGES.courtroom})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.07,
            filter: 'grayscale(70%) brightness(0.6)',
          }}
        />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="label-small mb-4">{t.retainr.mediaLabel}</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#F0EDE8] leading-[1.05]">
              {t.retainr.mediaHeading}
            </h2>
            <div className="gold-rule mt-6 mb-6" />
            <p className="text-[#DCCFAD] text-base md:text-lg leading-relaxed">{t.retainr.mediaSub}</p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            {SOCIALS.map((s, i) => (
              <motion.a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative border border-[rgba(196,163,90,0.3)] bg-[#0f1422] p-8 hover:border-[#C4A35A] transition-all duration-300 hover:-translate-y-1"
                data-testid={`social-card-${s.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 flex items-center justify-center border border-[rgba(196,163,90,0.4)] text-[#C4A35A] group-hover:bg-[#C4A35A] group-hover:text-[#0A0E1A] transition-colors">
                    <SocialIcon id={s.id} size={22} />
                  </div>
                  <ArrowUpRight size={18} className="text-[#8a7a54] group-hover:text-[#C4A35A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
                <div className="mt-8 text-[10px] tracking-[0.4em] uppercase text-[#8a7a54]">{s.label}</div>
                <div className="font-heading text-2xl text-[#F0EDE8] mt-1 group-hover:text-[#C4A35A] transition-colors">{s.handle}</div>
                <div className="mt-5 text-[11px] tracking-[0.3em] uppercase text-[#C4A35A]">{t.retainr.follow} &rarr;</div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetainRPage;
